import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Flame } from "lucide-react";
import { api } from "@/lib/auth";
import { REGION_LABELS } from "@/components/quantum/BodyMap";
import { VoiceButton } from "@/components/quantum/TeachingCards";

const SYNODIC = 29.530588853;
const moonAge = (d) => ((((d.getTime() - Date.UTC(2000, 0, 6, 18, 14)) / 86400000) % SYNODIC) + SYNODIC) % SYNODIC;
const moonOf = (d) => {
  const a = moonAge(d);
  return a < 1 || a > SYNODIC - 1 ? "new" : Math.abs(a - SYNODIC / 2) < 1 ? "full" : null;
};
const iso = (d) => d.toISOString().slice(0, 10);

// Practice calendar: every day you practised (either app), with new and full moons and your streak.
export const PracticeCalendar = () => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/practice/calendar", { params: { month } }).then(({ data: d }) => setData(d ?? null)).catch(() => setData(null));
  }, [month]);
  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7;
  const shift = (n) => setMonth(new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7));
  const today = iso(new Date());
  return (
    <section data-testid="practice-calendar" className="border border-[#D9B47A]/20 p-5">
      <div className="flex items-center justify-between">
        <button data-testid="calendar-prev" onClick={() => shift(-1)} aria-label="Previous month" className="p-2 text-[#D9B47A]"><ChevronLeft className="w-4 h-4" /></button>
        <p data-testid="calendar-month" className="font-display text-xl">{first.toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" })}</p>
        <button data-testid="calendar-next" onClick={() => shift(1)} aria-label="Next month" className="p-2 text-[#D9B47A]"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[11px] text-[#A8998C]">{["M", "T", "W", "T", "F", "S", "S"].map((d, k) => <span key={k}>{d}</span>)}</div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {Array.from({ length: lead }, (_, k) => <span key={`l${k}`} />)}
        {Array.from({ length: days }, (_, k) => {
          const d = new Date(Date.UTC(y, m - 1, k + 1));
          const n = data?.days?.[iso(d)] ?? 0;
          const moon = moonOf(d);
          return (
            <span key={k} data-testid={`calendar-day-${iso(d)}`} data-practised={n > 0} title={moon ? `${moon} moon` : undefined}
              className={`relative aspect-square flex items-center justify-center text-[12px] rounded-full ${n ? "bg-[#C2414F] text-[#F2E8DF]" : "text-[#CFC2B6]"} ${iso(d) === today ? "ring-1 ring-[#D9B47A]" : ""}`}>
              {k + 1}
              {moon && <span aria-hidden className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${moon === "full" ? "bg-[#F2E8DF] shadow-[0_0_6px_#F2E8DF]" : "border border-[#D9B47A] bg-[#0A0706]"}`} />}
            </span>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-[13px] text-[#CFC2B6]">
        <span data-testid="calendar-streak" className="inline-flex items-center gap-1.5"><Flame className="w-4 h-4 text-[#E8838E]" /> {data?.streak ?? 0}-day streak</span>
        <span data-testid="calendar-best">Best {data?.best ?? 0} days</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#F2E8DF]" /> Full moon</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full border border-[#D9B47A]" /> New moon</span>
      </div>
    </section>
  );
};

const Bars = ({ title, values, color, testId }) => {
  const rows = Object.entries(values ?? {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const max = Math.max(1, ...rows.map(([, v]) => v));
  return (
    <div data-testid={testId}>
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.2em]" style={{ color }}>{title}</p>
      {rows.length ? rows.map(([k, v]) => (
        <div key={k} className="mt-2 flex items-center gap-3 text-[13px]"><span className="w-28 shrink-0 text-[#CFC2B6] truncate">{REGION_LABELS[k] ?? k}</span><span className="h-2 rounded-full" style={{ width: `${(v / max) * 100}%`, background: color }} /></div>
      )) : <p className="mt-2 text-[13px] text-[#A8998C]">Nothing marked yet.</p>}
    </div>
  );
};

// Body insights: where current lives, where tension is held, and how much it eases.
export const BodyInsights = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/body/insights").then(({ data }) => setD(data ?? null)).catch(() => setD(null));
  }, []);
  if (!d?.checks) return <section data-testid="body-insights" className="border border-[#9DB4D6]/25 p-5 text-[14px] text-[#A8998C]">Check in with your body before and after a practice to grow your Body insights.</section>;
  const eased = d.tension_before ? Math.round(((d.tension_before - d.tension_after) / d.tension_before) * 100) : 0;
  return (
    <section data-testid="body-insights" className="border border-[#9DB4D6]/25 p-5">
      <p className="font-display text-2xl">Body insights</p>
      <p data-testid="body-insights-eased" className="mt-1 text-[14px] text-[#CFC2B6]">Across {d.checks} check-ins, your tension eased by <span className="text-[#9DB4D6]">{Math.max(0, eased)}%</span> from before to after.</p>
      <div className="mt-5 grid sm:grid-cols-3 gap-5">
        <Bars title="Where current lives" values={d.current} color="#D9B47A" testId="insights-current" />
        <Bars title="Where tension is held" values={d.tension} color="#9DB4D6" testId="insights-tension" />
        <Bars title="Where it eases most" values={d.eased} color="#E8838E" testId="insights-eased" />
      </div>
    </section>
  );
};

const MonthlyLetters = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/letters/monthly").then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);
  if (!list.length) return null;
  return (
    <section data-testid="monthly-letters" className="border border-[#D9B47A]/20 p-5">
      <p className="font-display text-2xl">Ariel’s letters to you</p>
      {list.map((l, k) => <details key={k} data-testid={`monthly-letter-${k}`} className="mt-3 border-t border-[#D9B47A]/10 pt-3" open={k === 0}><summary className="cursor-pointer font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">{l.label}</summary><p className="mt-3 whitespace-pre-line font-display italic text-[17px] leading-relaxed text-[#F2E8DF]">{l.text}</p></details>)}
    </section>
  );
};

const PracticeReflections = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/body/reflections").then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);
  if (!list.length) return null;
  return (
    <section data-testid="practice-reflections" className="border border-[#D9B47A]/20 p-5">
      <p className="font-display text-2xl">Ariel’s reflections on your practice</p>
      {list.map((r, k) => (
        <details key={k} data-testid={`practice-reflection-item-${k}`} className="mt-3 border-t border-[#D9B47A]/10 pt-3" open={k === 0}>
          <summary className="cursor-pointer font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">{r.practice || "Practice"} · {new Date(r.at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</summary>
          <div className="mt-2"><VoiceButton text={r.text} testId={`practice-reflection-voice-${k}`} /></div>
          <p className="mt-2 whitespace-pre-line font-display italic text-[17px] leading-relaxed text-[#F2E8DF]">{r.text}</p>
        </details>
      ))}
    </section>
  );
};

// Journal block shared by both apps.
export default function ProgressPanel() {
  return <div data-testid="progress-panel" className="max-w-xl mx-auto px-5 mt-12 grid gap-6"><PracticeCalendar /><BodyInsights /><PracticeReflections /><MonthlyLetters /></div>;
}
